import React, { useContext, useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";

import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import ConfirmationModal from "../ConfirmationModal";

import { FormControl, Grid, InputLabel, MenuItem, Select } from "@material-ui/core";

// Gravação de áudio
import MicRecorder from "mic-recorder-to-mp3";
import { Mic, Stop } from "@material-ui/icons";

const path = require("path");

// Estilos de exemplo
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  amplitudeContainer: {
    marginTop: theme.spacing(2),
    width: "100%",
    height: "10px",
    backgroundColor: "#ccc",
    borderRadius: "4px",
    overflow: "hidden",
  },
  amplitudeBar: {
    height: "100%",
    backgroundColor: "#4caf50",
    transition: "width 0.1s linear",
  },
  recordingTime: {
    marginTop: theme.spacing(1),
    fontWeight: "bold",
  },
}));

// Instância do gravador será criada dinamicamente baseada no dispositivo
let Mp3Recorder = null;
let audioConfig = null;

// Validação simples
const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required("Obrigatório"),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  // Estado inicial do QuickMessage
  const initialState = {
    shortcode: "",
    message: "",
    geral: false,
    status: true,
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);

  // Para anexar arquivo comum (imagem, pdf, etc.) ou áudio
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);

  // Para inserir variáveis no "message"
  const messageInputRef = useRef();

  // ----------------------------
  // ESTADOS E REFS PARA GRAVAÇÃO
  // ----------------------------
  const [recording, setRecording] = useState(false);  // Indica se está gravando
  const [loading, setLoading] = useState(false);      // "Carregando" quando inicia/para

  // Contador de tempo (MM:SS)
  const [recordTime, setRecordTime] = useState("00:00");
  const startTimeRef = useRef(0);

  // Amplitude (barrinha)
  const [amplitude, setAmplitude] = useState(0);
  const audioContextRef = useRef(null);
  const analyzerRef = useRef(null);
  const dataArrayRef = useRef(null);
  const micStreamRef = useRef(null);

  // ----------------------------
  // BUSCA/ATUALIZA QUICKMESSAGE
  // ----------------------------
  useEffect(() => {
    if (!quickemessageId) {
      setQuickemessage(initialState);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/quick-messages/${quickemessageId}`);
        setQuickemessage((prev) => ({ ...prev, ...data }));
      } catch (err) {
        toastError(err);
      }
    })();
  }, [quickemessageId, open]);

  // Fecha o modal - se estiver gravando, cancela
  const handleClose = () => {
    if (recording) {
      handleCancelRecording();
    }
    setQuickemessage(initialState);
    setAttachment(null);
    onClose();
  };

  // Se clicou em "Apagar Mídia"
  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      if (attachmentFile.current) {
        attachmentFile.current.value = null;
      }
    }
    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({ ...prev, mediaPath: null }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload === "function") reload();
    }
  };

  // Lida com arquivo "comum" (fora da gravação)
  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  // ---------------------------
  // GRAVAÇÃO DE ÁUDIO
  // ---------------------------
  // Iniciar Gravação
  const handleStartRecording = async () => {
    setLoading(true);
    try {
      // 1. Define recording = true
      setRecording(true);

      // 2. Inicializa o gravador otimizado para o dispositivo
      if (!Mp3Recorder || !audioConfig) {
        const { getOptimizedAudioConfig } = await import('../../utils/audioDeviceDetection');
        const config = await getOptimizedAudioConfig();
        Mp3Recorder = new MicRecorder(config.micRecorder);
        audioConfig = config;
        console.log('🎤 Configuração de áudio otimizada (QuickMessage):', {
          device: config.deviceType,
          bitRate: config.micRecorder.bitRate,
          sampleRate: config.micRecorder.sampleRate,
          channels: config.micRecorder.channels
        });
      }

      // 3. Pede permissão do microfone com configurações otimizadas
      const { getOptimizedMediaConstraints } = await import('../../utils/audioDeviceDetection');
      const mediaConstraints = await getOptimizedMediaConstraints();
      micStreamRef.current = await navigator.mediaDevices.getUserMedia(mediaConstraints);

      // 4. Cria AudioContext + Analyser com configurações otimizadas
      const { createOptimizedAudioContext, configureOptimizedAnalyser } = await import('../../utils/audioDeviceDetection');
      audioContextRef.current = await createOptimizedAudioContext();
      analyzerRef.current = audioContextRef.current.createAnalyser();
      await configureOptimizedAnalyser(analyzerRef.current);
      dataArrayRef.current = new Uint8Array(analyzerRef.current.frequencyBinCount);

      const source = audioContextRef.current.createMediaStreamSource(micStreamRef.current);
      source.connect(analyzerRef.current);

      // 5. Zera timer e amplitude
      setRecordTime("00:00");
      setAmplitude(0);
      startTimeRef.current = Date.now();

      // 6. Dispara loop para atualizar amplitude e tempo
      requestAnimationFrame(updateAmplitudeAndTime);

      // 7. Finalmente, inicia o Mp3Recorder
      await Mp3Recorder.start();

    } catch (err) {
      console.error('Erro ao iniciar gravação (QuickMessage):', err);
      toastError(err);
      setRecording(false);
    }
    setLoading(false);
  };

  // Loop de amplitude e contador de tempo
  const updateAmplitudeAndTime = () => {
    if (!recording || !analyzerRef.current) return; // se parou, sai

    // Amplitude
    analyzerRef.current.getByteTimeDomainData(dataArrayRef.current);
    let sum = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      sum += Math.abs(dataArrayRef.current[i] - 128);
    }
    const avgAmplitude = sum / dataArrayRef.current.length;
    setAmplitude(avgAmplitude); // valor de 0..128

    // Tempo decorrido
    const elapsed = Date.now() - startTimeRef.current; 
    const secs = Math.floor(elapsed / 1000);
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;

    const minStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const secStr = seconds < 10 ? `0${seconds}` : `${seconds}`;
    setRecordTime(`${minStr}:${secStr}`);

    // Chama outra vez no próximo frame
    requestAnimationFrame(updateAmplitudeAndTime);
  };

  // Parar Gravação (gera arquivo final)
  const handleStopRecording = async () => {
    setLoading(true);
    try {
      // 1. Para o gravador e obtém o blob
      const [buffer, blob] = await Mp3Recorder.stop().getMp3();

      // 2. Validação baseada na configuração do dispositivo
      const minSize = audioConfig?.deviceType === 'mobile' ? 30000 : 50000;
      if (blob.size < minSize) {
        toast.error("Gravação muito curta. Grave pelo menos 1 segundo de áudio.");
        setLoading(false);
        setRecording(false);
        stopAudioStream();
        return;
      }

      // 3. Log da qualidade do áudio para debug
      console.log('🎵 Áudio gravado (QuickMessage):', {
        size: `${(blob.size / 1024).toFixed(2)} KB`,
        device: audioConfig?.deviceType,
        quality: audioConfig?.micRecorder.bitRate + 'kbps'
      });

      // 4. Gera um nome aleatório
      const randomNumber = Math.floor(Math.random() * 1000000);
      const fileName = `gravacao${randomNumber}.mp3`;

      // 5. Cria o arquivo e salva em 'attachment'
      const audioFile = new File([blob], fileName, { type: blob.type });
      setAttachment(audioFile);
    } catch (err) {
      console.error('Erro ao parar gravação (QuickMessage):', err);
      toastError(err);
    } finally {
      setLoading(false);
      setRecording(false);
      stopAudioStream();
    }
  };

  // Cancelar gravação (descarta)
  const handleCancelRecording = () => {
    if (recording) {
      Mp3Recorder.stop(); // para sem gerar mp3
    }
    setRecording(false);
    setAmplitude(0);
    setRecordTime("00:00");
    stopAudioStream();
  };

  // Encerrar stream do microfone e fechar audioContext
  const stopAudioStream = () => {
    if (micStreamRef.current) {
      micStreamRef.current.getAudioTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyzerRef.current = null;
    dataArrayRef.current = null;
  };

  // ---------------------------
  // SALVAR QuickMessage
  // ---------------------------
  const handleSaveQuickeMessage = async (values) => {
    // Se estiver gravando, avisa e não prossegue
    if (recording) {
      toast.warning("Pare a gravação antes de concluir!");
      return;
    }

    const quickemessageData = {
      ...values,
      isMedia: true,
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
        ? path.basename(values.mediaPath).replace(/ /g, "_")
        : null,
    };

    try {
      // Se já existir, atualiza
      if (quickemessageId) {
        await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
        if (attachment) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(`/quick-messages/${quickemessageId}/media-upload`, formData);
        }
      } else {
        // Se for novo
        const { data } = await api.post("/quick-messages", quickemessageData);
        if (attachment) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(`/quick-messages/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload === "function") reload();
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  // Inserir variáveis no campo message
  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const textBefore = el.value.substring(0, start);
    const textAfter = el.value.substring(end);
    const newText = `${textBefore}${msgVar}${textAfter}`;
    setValueFunc("message", newText);

    // Ajustar cursor
    await new Promise((r) => setTimeout(r, 100));
    el.setSelectionRange(start + msgVar.length, start + msgVar.length);
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
        <DialogTitle id="form-dialog-title">
          {quickemessageId
            ? i18n.t("quickMessages.dialog.edit")
            : i18n.t("quickMessages.dialog.add")}
        </DialogTitle>

        {/* Input invisível p/ anexo */}
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>

        <Formik
          initialValues={quickemessage}
          enableReinitialize
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            handleSaveQuickeMessage(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      autoFocus
                      label={i18n.t("quickMessages.dialog.shortcode")}
                      name="shortcode"
                      disabled={
                        quickemessageId && values.visao && !values.geral && values.userId !== user.id
                      }
                      error={touched.shortcode && Boolean(errors.shortcode)}
                      helperText={touched.shortcode && errors.shortcode}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("quickMessages.dialog.message")}
                      name="message"
                      inputRef={messageInputRef}
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      variant="outlined"
                      margin="dense"
                      disabled={
                        quickemessageId && values.visao && !values.geral && values.userId !== user.id
                      }
                      multiline
                      rows={7}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MessageVariablesPicker
                      disabled={
                        isSubmitting ||
                        (quickemessageId && values.visao && !values.geral && values.userId !== user.id)
                      }
                      onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                    />
                  </Grid>

                  {/* Exemplo de 'visão' e 'geral': remova se não usar */}
                  <Grid item xs={12}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="visao-selection-label">
                        {i18n.t("quickMessages.dialog.visao")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("quickMessages.dialog.visao")}
                        placeholder={i18n.t("quickMessages.dialog.visao")}
                        labelId="visao-selection-label"
                        id="visao"
                        name="visao"
                        onChange={(e) => setFieldValue("visao", e.target.value === "true")}
                        error={touched.visao && Boolean(errors.visao)}
                        value={values.visao ? "true" : "false"}
                        disabled={
                          quickemessageId && values.visao && !values.geral && values.userId !== user.id
                        }
                      >
                        <MenuItem value={"true"}>{i18n.t("announcements.active")}</MenuItem>
                        <MenuItem value={"false"}>{i18n.t("announcements.inactive")}</MenuItem>
                      </Field>
                    </FormControl>

                    {values.visao === true && (
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel id="geral-selection-label">
                          {i18n.t("quickMessages.dialog.geral")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("quickMessages.dialog.geral")}
                          placeholder={i18n.t("quickMessages.dialog.geral")}
                          labelId="novo-item-selection-label"
                          id="geral"
                          name="geral"
                          value={values.geral ? "true" : "false"}
                          error={touched.geral && Boolean(errors.geral)}
                          disabled={
                            quickemessageId &&
                            values.visao &&
                            !values.geral &&
                            values.userId !== user.id
                          }
                        >
                          <MenuItem value={"true"}>{i18n.t("announcements.active")}</MenuItem>
                          <MenuItem value={"false"}>{i18n.t("announcements.inactive")}</MenuItem>
                        </Field>
                      </FormControl>
                    )}
                  </Grid>

                  {/* Se já tem anexo (imagem/doc ou áudio) */}
                  {(quickemessage.mediaPath || attachment) && (
                    <Grid item xs={12}>
                      <Button startIcon={<AttachFileIcon />}>
                        {attachment ? attachment.name : quickemessage.mediaName}
                      </Button>
                      <IconButton
                        onClick={() => setConfirmationOpen(true)}
                        color="secondary"
                        disabled={
                          quickemessageId &&
                          values.visao &&
                          !values.geral &&
                          values.userId !== user.id
                        }
                      >
                        <DeleteOutlineIcon color="secondary" />
                      </IconButton>
                    </Grid>
                  )}

                  {/* Se não tem anexo, mostra botões de Anexar e Gravar */}
                  {!attachment && !quickemessage.mediaPath && (
                    <Grid item xs={12}>
                      <Button
                        color="primary"
                        onClick={() => attachmentFile.current.click()}
                        disabled={
                          isSubmitting ||
                          (quickemessageId && values.visao && !values.geral && values.userId !== user.id)
                        }
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                      >
                        ANEXAR ARQUIVO
                      </Button>

                      <Button
                        style={{ marginLeft: 10 }}
                        variant="outlined"
                        color="secondary"
                        onClick={recording ? handleStopRecording : handleStartRecording}
                        disabled={
                          isSubmitting ||
                          (quickemessageId && values.visao && !values.geral && values.userId !== user.id)
                        }
                        startIcon={recording ? <Stop /> : <Mic />}
                      >
                        {loading
                          ? "Carregando..."
                          : recording
                          ? "PARAR GRAVAÇÃO"
                          : "GRAVAR ÁUDIO"}
                      </Button>

                      {/* Enquanto grava, exibe barra e contador */}
                      {recording && (
                        <>
                          <div className={classes.amplitudeContainer}>
                            <div
                              className={classes.amplitudeBar}
                              style={{
                                width: `${(amplitude / 128) * 100}%`,
                              }}
                            />
                          </div>
                          <div className={classes.recordingTime}>{recordTime}</div>
                        </>
                      )}
                    </Grid>
                  )}

                  {/* Se já gravou, exibe prévia do áudio */}
                  {attachment && attachment.type?.includes("audio") && !recording && (
                    <Grid item xs={12}>
                      <p style={{ fontWeight: "bold" }}>Áudio gravado: {attachment.name}</p>
                      <audio
                        controls
                        src={URL.createObjectURL(attachment)}
                        style={{ width: "100%" }}
                      />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("quickMessages.buttons.cancel")}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {quickemessageId
                    ? i18n.t("quickMessages.buttons.edit")
                    : i18n.t("quickMessages.buttons.add")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickMessageDialog;
